import { useState } from "react";
import SlotPicker from "@/components/forms/SlotPicker";
import CalendlyEmbed from "@/components/forms/CalendlyEmbed";
import Calendlo from "@/components/forms/Calendlo";
import { optionsFor, assetUrl } from "@/lib/forms";
import { Check, Star } from "lucide-react";

const L = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function Choice({ b, value, onChange, accent, multi, labels, radius }) {
  const opts = optionsFor(b);
  const [otherText, setOtherText] = useState(() => (value && !multi && !opts.includes(value) ? value : ""));
  const sel = multi ? value || [] : value;
  const isOn = (o) => (multi ? sel.includes(o) : sel === o);
  const pick = (o) => onChange(multi ? (isOn(o) ? sel.filter((x) => x !== o) : [...sel, o]) : o);
  const pics = b.type === "picture_choice";
  const imgs = b.option_images || {};
  const otherOn = !multi && b.allow_other && value && !opts.includes(value);
  return (
    <div className={pics ? "grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-xl" : "flex flex-col gap-2 max-w-md w-full"}>
      {opts.map((o, i) => (
        <button key={`${i}-${o}`} type="button" onClick={() => pick(o)} data-testid={`opt-${b.id}-${i}`}
          className={`tf-choice ${radius} text-left transition-colors ${pics ? "p-2 flex flex-col gap-2" : "flex items-center gap-3 px-3 py-2.5"}`}
          style={isOn(o) ? { borderColor: accent, background: `${accent}1f` } : undefined}>
          {pics && <div className="aspect-square w-full rounded overflow-hidden bg-white/5">{imgs[o] && <img src={assetUrl(imgs[o])} alt={o} className="w-full h-full object-cover" />}</div>}
          <span className="flex items-center gap-2 w-full">
            <span className="w-6 h-6 shrink-0 rounded border text-[11px] font-mono flex items-center justify-center"
              style={isOn(o) ? { background: accent, color: "#000", borderColor: accent } : { borderColor: "currentColor", opacity: 0.6 }}>{L[i]}</span>
            <span className="flex-1 text-[15px] break-words min-w-0">{o}</span>
            {isOn(o) && <Check size={16} style={{ color: accent }} />}
          </span>
        </button>
      ))}
      {b.allow_other && !multi && !pics && (
        <input className={`tf-choice ${radius} px-3 py-2.5 text-[15px] bg-transparent`} placeholder={`${labels.other} — ${labels.other_placeholder}`}
          value={otherText} style={otherOn ? { borderColor: accent } : undefined} data-testid={`opt-${b.id}-other`}
          onChange={(e) => { setOtherText(e.target.value); onChange(e.target.value); }} />
      )}
      {multi && <div className="text-[11px] opacity-50">{labels.choose_many}</div>}
    </div>
  );
}

function Scale({ b, value, onChange, accent, radius }) {
  if (b.type === "rating") {
    const n = Number(b.scale_max) || 5;
    return (
      <div className="flex gap-2" data-testid={`rating-${b.id}`}>
        {Array.from({ length: n }, (_, i) => i + 1).map((k) => (
          <button key={k} type="button" onClick={() => onChange(k)} data-testid={`rating-${b.id}-${k}`} className="p-1 transition-transform hover:scale-110">
            <Star size={34} strokeWidth={1.2} style={{ color: accent }} fill={value >= k ? accent : "transparent"} />
          </button>
        ))}
      </div>
    );
  }
  const min = b.type === "nps" ? 0 : Number(b.scale_min ?? 1);
  const max = b.type === "nps" ? 10 : Number(b.scale_max ?? 10);
  const nums = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  return (
    <div className="max-w-xl" data-testid={`scale-${b.id}`}>
      <div className="grid grid-cols-6 sm:flex gap-1">
        {nums.map((k) => (
          <button key={k} type="button" onClick={() => onChange(k)} data-testid={`scale-${b.id}-${k}`}
            className={`tf-choice ${radius} sm:flex-1 py-2.5 text-sm transition-colors`}
            style={value === k ? { background: accent, color: "#000", borderColor: accent } : undefined}>{k}</button>
        ))}
      </div>
      <div className="flex justify-between text-[11px] opacity-60 mt-2"><span>{b.scale_label_left}</span><span>{b.scale_label_right}</span></div>
    </div>
  );
}

export default function PublicField({ b, value, onChange, accent, slug, autoFocus, reloadKey, labels, radius, preview, prefill, vars }) {
  const ph = b.placeholder || labels.type_placeholder;
  const common = { autoFocus, "data-testid": `input-${b.id}` };
  const text = (type, placeholder = ph, cls = "", extra = {}) => (
    <input type={type} className={`tf-input text-lg sm:text-xl w-full min-w-0 ${cls}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} {...extra} {...common} />
  );
  const LOCKED = { url: ["url", "https://", { inputMode: "url" }], email: ["email", "name@example.com", { inputMode: "email" }], phone: ["tel", "+91 98765 43210", { inputMode: "tel" }], number: ["text", ph, { inputMode: "decimal" }], letters: ["text", ph, {}] };
  if (b.type === "short_text" && LOCKED[b.answer_type]) {
    const [t, p, x] = LOCKED[b.answer_type];
    return text(t, b.placeholder || p, "", x);
  }
  switch (b.type) {
    case "long_text":
      return <textarea rows={3} className="tf-input text-lg sm:text-xl resize-none w-full min-w-0" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={ph} {...common} />;
    case "email": return text("email", b.placeholder || "name@example.com");
    case "phone": return text("tel", b.placeholder || "+91 98765 43210");
    case "url": return text("url", b.placeholder || "https://");
    case "number": return text("number");
    case "date": return text("date", ph, "max-w-xs");
    case "dropdown":
      return (
        <select className="tf-input text-lg sm:text-xl w-full max-w-md" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`input-${b.id}`}>
          <option value="">{b.placeholder || labels.select_placeholder}</option>
          {optionsFor(b).map((o, i) => <option key={`${i}-${o}`} value={o}>{o}</option>)}
        </select>
      );
    case "single_choice": case "yes_no": case "picture_choice":
      return <Choice b={b} value={value} onChange={onChange} accent={accent} labels={labels} radius={radius} />;
    case "multi_choice":
      return <Choice b={b} value={value} onChange={onChange} accent={accent} labels={labels} radius={radius} multi />;
    case "rating": case "opinion_scale": case "nps":
      return <Scale b={b} value={value} onChange={onChange} accent={accent} radius={radius} />;
    case "consent":
      return (
        <div className="flex gap-2">
          {[[true, b.button_accept || labels.consent_accept], [false, b.button_decline || labels.consent_decline]].map(([v, l]) => (
            <button key={String(v)} type="button" onClick={() => onChange(v)} data-testid={`consent-${b.id}-${v}`}
              className={`tf-choice ${radius} px-4 py-2.5 text-[15px]`} style={value === v ? { borderColor: accent, background: `${accent}1f` } : undefined}>{l}</button>
          ))}
        </div>
      );
    case "calendar_meet":
      return preview ? <div className="text-sm opacity-60">Live time slots appear on the published form.</div>
        : <SlotPicker slug={slug} blockId={b.id} value={value} onChange={onChange} accent={accent} reloadKey={reloadKey} labels={labels} />;
    case "calendlo":
      return <Calendlo b={b} slug={slug} value={value} onChange={onChange} accent={accent} reloadKey={reloadKey} preview={preview} vars={vars} />;
    case "calendly":
      return <CalendlyEmbed b={b} value={value} onChange={onChange} prefill={prefill} preview={preview} />;
    default: return text("text");
  }
}
